import { useState, useEffect, useRef } from 'react';
import { Medal, ArrowUp, ArrowDown, FileDown, FileText, ChevronUp, ChevronDown, SlidersHorizontal, Search } from 'lucide-react';
import { Negotiator } from '../../types/negotiator';
import { formatCurrency } from '../../utils/format';
import { getStatusBadgeClass, getCompletionColor } from '../../utils/colors';
import NegotiatorDetailsModal from './NegotiatorDetailsModal';
import ExportModal from './ExportModal';
import ColumnConfigPanel, { ColumnConfig } from './ColumnConfigPanel';
import { useNegotiatorTransactions } from '../../hooks/useNegotiatorTransactions';
import { NegotiatorTransaction } from '../../types/transaction';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { downloadStyledExcel, CURRENCY_FMT, PCT_FMT } from '../../utils/excelExport';
import { supabase } from '../../lib/supabase';
import { isPotentialVisible, getPeriodEndDate } from '../../utils/period';

const STORAGE_KEY = 'kprod-classement-columns-v2';

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'position',          label: '#',              visible: true, fixed: true },
  { id: 'name',              label: 'Négociateur',    visible: true },
  { id: 'status',            label: 'Statut',         visible: true },
  { id: 'objective',         label: 'Objectif',       visible: true },
  { id: 'sales',             label: 'Hono Réalisé',   visible: true },
  { id: 'remaining',         label: 'Reste',          visible: true },
  { id: 'completion',        label: '% Réalisation',  visible: true },
  { id: 'vendeur_apporteur', label: '100%',           visible: true },
  { id: 'vendeur',           label: 'Vendeur',        visible: true },
  { id: 'apporteur',         label: 'Apporteur',      visible: true },
  { id: 'potential',         label: 'Hono Potentiel', visible: true },
  { id: 'cumulative',        label: 'Hono Cumulé',    visible: true },
];

function loadColumnsFromStorage(): ColumnConfig[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMNS.map(c => ({ ...c }));
    const saved: ColumnConfig[] = JSON.parse(raw);
    const defaultMap = new Map(DEFAULT_COLUMNS.map(c => [c.id, c]));
    // Keep saved order/visibility but always use the latest label & fixed from DEFAULT_COLUMNS
    const merged = saved
      .filter(c => defaultMap.has(c.id))
      .map(c => ({ ...defaultMap.get(c.id)!, visible: c.visible }));
    // Add any new default columns not yet in saved
    const savedIds = new Set(saved.map(c => c.id));
    DEFAULT_COLUMNS.filter(c => !savedIds.has(c.id)).forEach(c => merged.push({ ...c }));
    return merged;
  } catch {
    return DEFAULT_COLUMNS.map(c => ({ ...c }));
  }
}

interface TopPositionTableProps {
  negotiators: Negotiator[];
  period: 'year' | 'quarter' | 'month';
  selectedPeriod: string;
  fiscalYear: number;
}

type SortField = 'name' | 'status' | 'potential' | 'sales' | 'cumulative' | 'objective' | 'remaining' | 'completion' | 'vendeur_apporteur_count' | 'vendeur_count' | 'apporteur_count';
type SortDirection = 'asc' | 'desc';

export default function TopPositionTable({ negotiators, period, selectedPeriod, fiscalYear }: TopPositionTableProps) {
  const [selectedNegotiator, setSelectedNegotiator] = useState<Negotiator | null>(null);
  const [transactions, setTransactions] = useState<NegotiatorTransaction[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('completion');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const { loadTransactions, loading } = useNegotiatorTransactions();
  const [exporting, setExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>(loadColumnsFromStorage);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  const columnPanelRef = useRef<HTMLDivElement>(null);

  // Persist to localStorage whenever columns change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  }, [columns]);

  // Close panel on outside click
  useEffect(() => {
    if (!showColumnPanel) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (columnPanelRef.current && !columnPanelRef.current.contains(e.target as Node)) {
        setShowColumnPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnPanel]);

  const hiddenCount = columns.filter(c => !c.visible).length;
  const visibleColumns = columns.filter(c => c.visible);

  const showPotential = isPotentialVisible(getPeriodEndDate(period, selectedPeriod, fiscalYear));

  const getFormattedPeriod = () => {
    if (period === 'year') {
      return `Année ${fiscalYear}-${fiscalYear + 1}`;
    } else if (period === 'quarter') {
      const quarterMap = {
        '1': 'Trimestre 1 (Avril - Juin)',
        '2': 'Trimestre 2 (Juillet - Septembre)',
        '3': 'Trimestre 3 (Octobre - Décembre)',
        '4': 'Trimestre 4 (Janvier - Mars)'
      };
      const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
      return `${quarterMap[selectedPeriod as keyof typeof quarterMap]} ${year}`;
    } else {
      const monthMap = {
        '04': 'Avril',
        '05': 'Mai',
        '06': 'Juin',
        '07': 'Juillet',
        '08': 'Août',
        '09': 'Septembre',
        '10': 'Octobre',
        '11': 'Novembre',
        '12': 'Décembre',
        '01': 'Janvier',
        '02': 'Février',
        '03': 'Mars'
      };
      const month = monthMap[selectedPeriod.padStart(2, '0') as keyof typeof monthMap];
      const year = parseInt(selectedPeriod) <= 3 ? fiscalYear + 1 : fiscalYear;
      return `${month} ${year}`;
    }
  };

  const handleExport = async (exportType: 'classement' | 'par-agence', company?: string) => {
    setExporting(true);
    try {
      if (exportType === 'classement') {
        await handleExportClassement();
      } else {
        await handleExportByAgency(company);
      }
      setIsExportModalOpen(false);
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportClassement = async () => {
    const objLabel = `Objectif ${period === 'year' ? 'Annuel' : period === 'quarter' ? 'Trim.' : 'Mensuel'}`;
    const columns = [
      { header: '#',            key: 'pos',       width: 6,  align: 'center' as const },
      { header: 'Négociateur',  key: 'name',      width: 28 },
      { header: 'Statut',       key: 'status',    width: 14 },
      { header: 'Hono Potentiel', key: 'potential', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Réalisé',   key: 'sales',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Hono Cumulé',    key: 'cumul',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: objLabel,          key: 'obj',       width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: 'Reste',           key: 'remaining', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
      { header: '% Réalisation',   key: 'rate',      width: 14, numFmt: PCT_FMT,      align: 'center' as const },
      { header: '100%',            key: 'va',        width: 8,  align: 'center' as const },
      { header: 'Vendeur',         key: 'vend',      width: 10, align: 'center' as const },
      { header: 'Apporteur',       key: 'apport',    width: 10, align: 'center' as const },
    ];

    const rows = filteredNegotiators.map((neg, i) => ({
      data: {
        pos:       i + 1,
        name:      `${neg.firstname} ${neg.lastname}`,
        status:    neg.status,
        potential: neg.potential_sales,
        sales:     neg.total_sales,
        cumul:     neg.potential_sales + neg.total_sales,
        obj:       neg.annual_objective,
        remaining: neg.remaining,
        rate:      parseFloat(neg.completion_rate.toFixed(1)),
        va:        (neg as any).vendeur_apporteur_count || 0,
        vend:      neg.vendeur_count,
        apport:    neg.apporteur_count,
      },
    }));

    const label = getFormattedPeriod().replace(/[/\s]/g, '_');
    await downloadStyledExcel(`top_position_${label}.xlsx`, 'Classement', columns, rows);
  };

  const handleExportByAgency = async (company?: string) => {
    try {
      // Récupérer les associations négociateurs-agences
      const { data: negotiatorAgencies, error: agenciesError } = await supabase
        .from('negotiator_agencies')
        .select('negotiator_id, agency')
        .order('agency');

      if (agenciesError) throw agenciesError;

      // Récupérer les informations des agences pour filtrer par société
      const { data: agencies, error: agencyInfoError } = await supabase
        .from('agencies')
        .select('name, company');

      if (agencyInfoError) throw agencyInfoError;

      // Créer un mapping agence -> société
      const agencyCompanyMap = new Map<string, string>();
      agencies?.forEach(agency => {
        agencyCompanyMap.set(agency.name, agency.company);
      });

      // Récupérer toutes les transactions pour la période
      let startDate: string;
      let endDate: string;

      if (period === 'year') {
        startDate = `${fiscalYear}-04-01`;
        endDate = `${fiscalYear + 1}-03-31`;
      } else if (period === 'quarter') {
        const quarterMap = {
          '1': { start: '04', end: '06' },
          '2': { start: '07', end: '09' },
          '3': { start: '10', end: '12' },
          '4': { start: '01', end: '03' }
        };
        const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
        const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
        startDate = `${year}-${quarter.start}-01`;
        endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
      } else {
        const month = selectedPeriod.padStart(2, '0');
        const year = parseInt(selectedPeriod) <= 3 ? fiscalYear + 1 : fiscalYear;
        startDate = `${year}-${month}-01`;
        endDate = `${year}-${month}-${new Date(year, parseInt(month), 0).getDate()}`;
      }

      const { data: transactions, error: transactionsError } = await supabase
        .from('transactions')
        .select(`
          id,
          fees_without_tax,
          status,
          compromise_date,
          transaction_negotiators (
            negotiator_id,
            role,
            percentage,
            agency
          ),
          transaction_retrocessions (
            amount
          )
        `)
        .or(`and(status.eq.potentiel),and(status.in.(compromis,acte,encaisse,cloture),compromise_date.gte.${startDate},compromise_date.lte.${endDate})`);

      if (transactionsError) throw transactionsError;

      // Créer un mapping négociateur -> agences
      const negotiatorAgencyMap = new Map<string, string[]>();
      negotiatorAgencies?.forEach(na => {
        // Filtrer par société si spécifié
        if (company && company !== 'all') {
          const agencyCompany = agencyCompanyMap.get(na.agency);
          if (agencyCompany !== company) return;
        }

        if (!negotiatorAgencyMap.has(na.negotiator_id)) {
          negotiatorAgencyMap.set(na.negotiator_id, []);
        }
        negotiatorAgencyMap.get(na.negotiator_id)?.push(na.agency);
      });

      // Grouper les négociateurs par agence avec leurs statistiques spécifiques à cette agence
      const negotiatorsByAgency = new Map<string, Array<{
        negotiator: Negotiator;
        total_sales: number;
        potential_sales: number;
        sales_count: number;
        vendeur_count: number;
        apporteur_count: number;
        autre_count: number;
      }>>();
      
      filteredNegotiators.forEach(neg => {
        const agencies = negotiatorAgencyMap.get(neg.id);
        if (!agencies || agencies.length === 0) {
          // Si pas d'agence et qu'on filtre par société, on ignore ce négociateur
          if (company && company !== 'all') return;
          
          if (!negotiatorsByAgency.has('Sans agence')) {
            negotiatorsByAgency.set('Sans agence', []);
          }
          negotiatorsByAgency.get('Sans agence')?.push({
            negotiator: neg,
            total_sales: neg.total_sales,
            potential_sales: neg.potential_sales,
            sales_count: neg.sales_count,
            vendeur_count: neg.vendeur_count,
            apporteur_count: neg.apporteur_count,
            autre_count: neg.autre_count || 0
          });
        } else {
          agencies.forEach(agencyName => {
            // Calculer les statistiques pour ce négociateur dans cette agence spécifique
            let agencyTotalSales = 0;
            let agencyPotentialSales = 0;
            let agencySalesCount = 0;
            let agencyVendeurCount = 0;
            let agencyApporteurCount = 0;
            let agencyAutreCount = 0;

            // Filtrer les transactions pour cette agence
            transactions?.forEach(transaction => {
              const negotiatorData = transaction.transaction_negotiators?.find(
                tn => tn.negotiator_id === neg.id && tn.agency === agencyName
              );

              if (negotiatorData) {
                const totalRetrocessions = transaction.transaction_retrocessions?.reduce(
                  (sum, r) => sum + r.amount, 0
                ) || 0;
                const netFeesWithoutTax = transaction.fees_without_tax - totalRetrocessions;
                const negotiatorAmount = (netFeesWithoutTax * negotiatorData.percentage) / 100;

                if (transaction.status === 'potentiel') {
                  agencyPotentialSales += negotiatorAmount;
                } else {
                  agencyTotalSales += negotiatorAmount;
                  agencySalesCount++;

                  // Compter les rôles
                  if (negotiatorData.role === 'vendeur') {
                    agencyVendeurCount++;
                  } else if (negotiatorData.role === 'apporteur') {
                    agencyApporteurCount++;
                  } else {
                    agencyAutreCount++;
                  }
                }
              }
            });

            if (!negotiatorsByAgency.has(agencyName)) {
              negotiatorsByAgency.set(agencyName, []);
            }
            
            negotiatorsByAgency.get(agencyName)?.push({
              negotiator: neg,
              total_sales: agencyTotalSales,
              potential_sales: agencyPotentialSales,
              sales_count: agencySalesCount,
              vendeur_count: agencyVendeurCount,
              apporteur_count: agencyApporteurCount,
              autre_count: agencyAutreCount
            });
          });
        }
      });

      const sortedAgencies = Array.from(negotiatorsByAgency.keys()).sort();

      const objLabel = `Objectif ${period === 'year' ? 'Annuel' : period === 'quarter' ? 'Trim.' : 'Mensuel'}`;
      const columns = [
        { header: 'Agence / Négociateur', key: 'name',      width: 30 },
        { header: 'Statut',               key: 'status',    width: 14 },
        { header: 'Hono Potentiel',       key: 'potential', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
        { header: 'Hono Réalisé',         key: 'sales',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
        { header: 'Hono Cumulé',          key: 'cumul',     width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
        { header: objLabel,               key: 'obj',       width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
        { header: 'Reste',                key: 'remaining', width: 18, numFmt: CURRENCY_FMT, align: 'right' as const },
        { header: '% Réalisation',        key: 'rate',      width: 14, numFmt: PCT_FMT,      align: 'center' as const },
        { header: '100%',                 key: 'va',        width: 8,  align: 'center' as const },
        { header: 'Vendeur',              key: 'vend',      width: 10, align: 'center' as const },
        { header: 'Apporteur',            key: 'apport',    width: 10, align: 'center' as const },
      ];

      const rows: { data: Record<string, string | number>; level?: 0 | 1 | 2; isSection?: boolean }[] = [];

      sortedAgencies.forEach(agencyName => {
        rows.push({ data: { name: agencyName, status: '', potential: '', sales: '', cumul: '', obj: '', remaining: '', rate: '', va: '', vend: '', apport: '' }, isSection: true });
        const agencyNegotiators = (negotiatorsByAgency.get(agencyName) || []).sort((a, b) => b.total_sales - a.total_sales);
        agencyNegotiators.forEach(({ negotiator, total_sales, potential_sales, sales_count, vendeur_count, apporteur_count }) => {
          const obj = negotiator.annual_objective;
          const rate = obj ? parseFloat(((total_sales / obj) * 100).toFixed(1)) : 0;
          rows.push({
            data: {
              name:      `${negotiator.firstname} ${negotiator.lastname}`,
              status:    negotiator.status,
              potential: potential_sales,
              sales:     total_sales,
              cumul:     potential_sales + total_sales,
              obj,
              remaining: obj - total_sales,
              rate,
              va:        sales_count,
              vend:      vendeur_count,
              apport:    apporteur_count,
            },
            level: 1,
          });
        });
      });

      const periodLabel = getFormattedPeriod().replace(/[/\s]/g, '_');
      const companyLabel = company && company !== 'all' ? `_${company}` : '';
      await downloadStyledExcel(`top_position_par_agence${companyLabel}_${periodLabel}.xlsx`, 'Par Agence', columns, rows);
    } catch (error) {
      console.error('Error exporting by agency:', error);
      throw error;
    }
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      const tables = document.querySelectorAll('.status-section');
      if (!tables.length) return;

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      let currentY = 10;

      pdf.setFontSize(16);
      pdf.text('Classement', 10, currentY);
      currentY += 10;

      pdf.setFontSize(12);
      const periodText = getFormattedPeriod();
      pdf.text(periodText, 10, currentY);
      currentY += 15;

      for (let i = 0; i < tables.length; i++) {
        const table = tables[i];
        
        const canvas = await html2canvas(table as HTMLElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (currentY + pdfHeight > pdf.internal.pageSize.getHeight() - 10) {
          pdf.addPage();
          currentY = 10;
        }

        pdf.addImage(imgData, 'PNG', 10, currentY, pdfWidth, pdfHeight);
        currentY += pdfHeight + 10;
      }

      const periodLabel = getFormattedPeriod().replace(/[\/\s]/g, '_');
      pdf.save(`top_position_${periodLabel}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleNegotiatorClick = async (negotiator: Negotiator) => {
    let startDate: string;
    let endDate: string;

    if (period === 'year') {
      startDate = `${fiscalYear}-04-01`;
      endDate = `${fiscalYear + 1}-03-31`;
    } else if (period === 'quarter') {
      const quarterMap = {
        '1': { start: '04', end: '06' },
        '2': { start: '07', end: '09' },
        '3': { start: '10', end: '12' },
        '4': { start: '01', end: '03' }
      };
      const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
      const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
      startDate = `${year}-${quarter.start}-01`;
      endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
    } else {
      const month = selectedPeriod.padStart(2, '0');
      const year = parseInt(selectedPeriod) <= 3 ? fiscalYear + 1 : fiscalYear;
      startDate = `${year}-${month}-01`;
      endDate = `${year}-${month}-${new Date(year, parseInt(month), 0).getDate()}`;
    }

    setSelectedNegotiator(negotiator);
    const transactions = await loadTransactions(negotiator.id, startDate, endDate);
    setTransactions(transactions);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const searchFiltered = searchQuery.trim()
    ? negotiators.filter(n => {
        const q = searchQuery.toLowerCase();
        return (
          `${n.firstname} ${n.lastname}`.toLowerCase().includes(q) ||
          `${n.lastname} ${n.firstname}`.toLowerCase().includes(q)
        );
      })
    : negotiators;

  const negotiatorsByStatus = searchFiltered.reduce((acc, negotiator) => {
    if (!acc[negotiator.status]) {
      acc[negotiator.status] = [];
    }
    acc[negotiator.status].push(negotiator);
    return acc;
  }, {} as Record<string, Negotiator[]>);

  const statusOrder = ['expert', 'senior', 'confirmé', 'conseiller', 'animateur', 'directeur', 'direction'];
  const sortedStatuses = Object.keys(negotiatorsByStatus).sort(
    (a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b)
  );

  const sortNegotiators = (negotiators: Negotiator[]) => {
    return [...negotiators].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`);
          break;
        case 'status':
          comparison = statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
          break;
        case 'potential':
          comparison = a.potential_sales - b.potential_sales;
          break;
        case 'sales':
          comparison = a.total_sales - b.total_sales;
          break;
        case 'cumulative':
          comparison = (a.potential_sales + a.total_sales) - (b.potential_sales + b.total_sales);
          break;
        case 'objective':
          comparison = a.annual_objective - b.annual_objective;
          break;
        case 'remaining':
          comparison = (a.annual_objective - a.total_sales) - (b.annual_objective - b.total_sales);
          break;
        case 'completion':
          comparison = a.completion_rate - b.completion_rate;
          break;
        case 'vendeur_apporteur_count':
          comparison = (a.vendeur_apporteur_count || 0) - (b.vendeur_apporteur_count || 0);
          break;
        case 'vendeur_count':
          comparison = a.vendeur_count - b.vendeur_count;
          break;
        case 'apporteur_count':
          comparison = a.apporteur_count - b.apporteur_count;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const filteredNegotiators = selectedStatus === 'all'
    ? searchFiltered
    : selectedStatus === 'everyone'
      ? sortNegotiators(searchFiltered)
      : sortNegotiators(searchFiltered.filter(n => n.status === selectedStatus));

  const renderSortIcon = (field: SortField) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc'
      ? <ChevronUp size={13} className="text-indigo-500" />
      : <ChevronDown size={13} className="text-indigo-500" />;
  };

  // ── Column helpers ──
  const sortFieldForColumn: Record<string, SortField> = {
    name: 'name',
    status: 'status',
    potential: 'potential',
    sales: 'sales',
    cumulative: 'cumulative',
    objective: 'objective',
    remaining: 'remaining',
    completion: 'completion',
    vendeur_apporteur: 'vendeur_apporteur_count',
    vendeur: 'vendeur_count',
    apporteur: 'apporteur_count',
  };

  const renderDynamicHeader = (col: ColumnConfig) => {
    if (col.id === 'position') {
      return (
        <th key="position" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">
          #
        </th>
      );
    }
    const field = sortFieldForColumn[col.id];
    const label = col.id === 'objective'
      ? `Objectif ${period === 'year' ? 'Annuel' : period === 'quarter' ? 'Trim.' : 'Mensuel'}`
      : col.label;
    if (!field) return null;
    return (
      <th
        key={col.id}
        className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {renderSortIcon(field)}
        </div>
      </th>
    );
  };

  // ── Avatar helpers ──
  const getAvatarBg = (status: string) => {
    switch (status) {
      case 'expert':     return 'bg-purple-700';
      case 'senior':     return 'bg-blue-700';
      case 'confirmé':   return 'bg-indigo-700';
      case 'conseiller': return 'bg-teal-700';
      case 'animateur':  return 'bg-emerald-700';
      case 'directeur':  return 'bg-slate-600';
      case 'direction':  return 'bg-amber-600';
      default:           return 'bg-slate-700';
    }
  };

  const getInitials = (firstname: string, lastname: string) =>
    `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase();

  // Display "—" for zero/null monetary values
  const moneyCell = (value: number, pillClass: string) => {
    if (!value || value === 0) {
      return <span className="text-slate-300 font-medium text-sm">—</span>;
    }
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${pillClass}`}>
        {formatCurrency(value)}
      </span>
    );
  };

  const renderDynamicCell = (col: ColumnConfig, negotiator: Negotiator, index: number) => {
    switch (col.id) {
      case 'position':
        return (
          <td key="position" className="px-4 py-4 whitespace-nowrap">
            {renderPositionCell(index, negotiator)}
          </td>
        );
      case 'name':
        return (
          <td key="name" className="px-4 py-4 whitespace-nowrap">
            <div className="flex flex-col">
              <span className="text-sm font-bold text-slate-800">
                {negotiator.firstname} {negotiator.lastname}
              </span>
              <span className="text-xs text-indigo-500 mt-0.5 font-medium">
                Voir le détail →
              </span>
            </div>
          </td>
        );
      case 'status':
        return (
          <td key="status" className="px-4 py-4 whitespace-nowrap">
            <span className={`px-2.5 py-1 inline-flex text-xs font-semibold rounded-full border ${getStatusBadgeClass(negotiator.status)}`}>
              {negotiator.status}
            </span>
          </td>
        );
      case 'potential':
        return (
          <td key="potential" className="px-4 py-4 whitespace-nowrap">
            {showPotential
              ? moneyCell(negotiator.potential_sales, 'border-amber-300 text-amber-700 bg-amber-50')
              : <span className="text-slate-300 text-sm">—</span>}
          </td>
        );
      case 'sales':
        return (
          <td key="sales" className="px-4 py-4 whitespace-nowrap">
            {moneyCell(negotiator.total_sales, 'border-emerald-300 text-emerald-700 bg-emerald-50')}
          </td>
        );
      case 'cumulative':
        return (
          <td key="cumulative" className="px-4 py-4 whitespace-nowrap">
            {moneyCell(
              showPotential
                ? negotiator.potential_sales + negotiator.total_sales
                : negotiator.total_sales,
              'border-indigo-300 text-indigo-700 bg-indigo-50'
            )}
          </td>
        );
      case 'objective':
        return (
          <td key="objective" className="px-4 py-4 whitespace-nowrap">
            {negotiator.annual_objective
              ? moneyCell(negotiator.annual_objective, 'border-slate-300 text-slate-600 bg-slate-50')
              : <span className="text-slate-300 text-sm">—</span>
            }
          </td>
        );
      case 'remaining':
        return (
          <td key="remaining" className="px-4 py-4 whitespace-nowrap">
            {negotiator.annual_objective ? (
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                negotiator.remaining > 0
                  ? 'bg-red-50 text-red-700 border-red-200'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-200'
              }`}>
                <span>{negotiator.remaining > 0 ? '↘' : '↗'}</span>
                {formatCurrency(Math.abs(negotiator.remaining))}
              </span>
            ) : (
              <span className="text-slate-300 text-sm">—</span>
            )}
          </td>
        );
      case 'completion':
        return (
          <td key="completion" className="px-4 py-4 whitespace-nowrap">
            {negotiator.annual_objective ? (
              <div className="flex flex-col gap-1.5">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border w-fit ${
                  negotiator.completion_rate >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                  negotiator.completion_rate >= 75  ? 'bg-blue-50 text-blue-700 border-blue-200' :
                  negotiator.completion_rate >= 50  ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                      'bg-red-50 text-red-700 border-red-200'
                }`}>
                  {negotiator.completion_rate.toFixed(1)}%
                </span>
                <div className="h-1 w-20 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      negotiator.completion_rate >= 100 ? 'bg-emerald-400' :
                      negotiator.completion_rate >= 75  ? 'bg-blue-400' :
                      negotiator.completion_rate >= 50  ? 'bg-amber-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${Math.min(negotiator.completion_rate, 100)}%` }}
                  />
                </div>
              </div>
            ) : (
              <span className="text-slate-300 text-sm">—</span>
            )}
          </td>
        );
      case 'vendeur_apporteur':
        return (
          <td key="vendeur_apporteur" className="px-4 py-4 whitespace-nowrap">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-xs font-bold text-slate-600 border border-slate-200">
              {(negotiator as any).vendeur_apporteur_count || 0}
            </span>
          </td>
        );
      case 'vendeur':
        return (
          <td key="vendeur" className="px-4 py-4 whitespace-nowrap">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 text-xs font-bold text-emerald-700 border border-emerald-200">
              {negotiator.vendeur_count}
            </span>
          </td>
        );
      case 'apporteur':
        return (
          <td key="apporteur" className="px-4 py-4 whitespace-nowrap">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-50 text-xs font-bold text-amber-700 border border-amber-200">
              {negotiator.apporteur_count}
            </span>
          </td>
        );
      default:
        return null;
    }
  };

  const renderPositionCell = (index: number, negotiator: Negotiator) => {
    const medalRings = [
      'ring-2 ring-amber-400 ring-offset-1',
      'ring-2 ring-slate-400 ring-offset-1',
      'ring-2 ring-orange-400 ring-offset-1',
    ];
    const medalIcons = ['🥇', '🥈', '🥉'];

    const initials = getInitials(negotiator.firstname, negotiator.lastname);
    const avatarBg = getAvatarBg(negotiator.status);

    return (
      <div className="flex items-center gap-1">
        {/* Rank number */}
        <div className="w-6 text-center">
          {index < 3 ? (
            <span className="text-base leading-none">{medalIcons[index]}</span>
          ) : (
            <span className="text-xs font-bold text-slate-400">{index + 1}</span>
          )}
        </div>
        {/* Avatar */}
        <div className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${avatarBg} ${index < 3 ? medalRings[index] : ''}`}>
          <span className="text-xs font-bold text-white tracking-wide">{initials}</span>
        </div>
      </div>
    );
  };

  const renderNegotiatorRow = (negotiator: Negotiator, index: number) => (
    <tr
      key={negotiator.id}
      className="group hover:bg-indigo-50/30 cursor-pointer transition-colors border-b border-slate-100 last:border-0"
      onClick={() => handleNegotiatorClick(negotiator)}
    >
      {visibleColumns.map(col => renderDynamicCell(col, negotiator, index))}
    </tr>
  );

  const tableHead = (
    <thead className="bg-slate-50 border-b border-slate-200">
      <tr>
        {visibleColumns.map(col => renderDynamicHeader(col))}
      </tr>
    </thead>
  );

  return (
    <>
      {/* ── Filter & Actions bar ── */}
      <div className="relative" ref={columnPanelRef}>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 bg-indigo-50 border-b border-indigo-100">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
              <Medal size={16} className="text-indigo-600" />
            </div>
            <span className="text-sm font-semibold text-indigo-800 flex-1">{getFormattedPeriod()}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsExportModalOpen(true)}
                disabled={exporting}
                title="Exporter Excel"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <FileDown size={15} />
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exporting}
                title="Exporter PDF"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                <FileText size={15} />
              </button>
            </div>
          </div>
          <div className="px-5 py-3 flex items-center gap-3">
            <label className="text-xs font-medium text-slate-500 shrink-0">Type de classement</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="max-w-xs px-3 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
            >
              <option value="everyone">Classement global (tous statuts)</option>
              <option value="all">Classement par statut</option>
              {sortedStatuses.map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>

            {/* Search bar */}
            <div className="relative flex-1 max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un négociateur…"
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <span className="text-xs font-bold">✕</span>
                </button>
              )}
            </div>

            <div className="ml-auto flex items-center gap-3">
              <div className="w-9" />
            </div>
          </div>
        </div>

        {/* ── Bouton Colonnes — hors du overflow-hidden pour que le panneau ne soit pas coupé ── */}
        <div className="absolute right-4 bottom-3 z-10">
          <button
            onClick={() => setShowColumnPanel(p => !p)}
            title="Gérer les colonnes"
            className={`relative inline-flex items-center justify-center w-8 h-8 rounded-xl border transition-colors ${
              showColumnPanel
                ? 'bg-[#2d3a5e] text-white border-[#2d3a5e]'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <SlidersHorizontal size={15} />
            {hiddenCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-indigo-500 text-white text-[9px] font-bold">
                {hiddenCount}
              </span>
            )}
          </button>

          {showColumnPanel && (
            <div className="absolute right-0 top-full mt-2 z-50">
              <ColumnConfigPanel
                columns={columns}
                onColumnsChange={setColumns}
                defaultColumns={DEFAULT_COLUMNS}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Table(s) ── */}
      {selectedStatus === 'all' ? (
        <div className="space-y-6">
          {sortedStatuses.map(status => (
            <div key={status} className="status-section">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    {tableHead}
                    <tbody className="bg-white">
                      {negotiatorsByStatus[status].map((negotiator, index) =>
                        renderNegotiatorRow(negotiator, index)
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden status-section">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              {tableHead}
              <tbody className="bg-white">
                {filteredNegotiators.map((negotiator, index) =>
                  renderNegotiatorRow(negotiator, index)
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <NegotiatorDetailsModal
        isOpen={selectedNegotiator !== null}
        onClose={() => setSelectedNegotiator(null)}
        negotiatorName={selectedNegotiator ? `${selectedNegotiator.firstname} ${selectedNegotiator.lastname}` : ''}
        transactions={transactions}
      />

      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        exporting={exporting}
      />
    </>
  );
}